import React, { useState, useEffect } from "react";
import {
  AlertCircle,
  CircleDollarSign,
  CalendarClock,
  Share2,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateToBritish } from "@/lib/dateUtils";
import { OperationsDashboardService } from "@/services/operationsDashboardService";
import { AffiliatePartner } from "@/types/operations";
import SearchFilter from "./SearchFilter";
import DateRangeFilter from "./DateRangeFilter";

const AffiliatePartnersList: React.FC = () => {
  const [affiliates, setAffiliates] = useState<AffiliatePartner[]>([]);
  const [filteredAffiliates, setFilteredAffiliates] = useState<
    AffiliatePartner[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dateRange, setDateRange] = useState<{ start?: Date; end?: Date }>({});

  // Fetch affiliates on component mount
  useEffect(() => {
    const fetchAffiliatePartners = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await OperationsDashboardService.getAffiliatePartners();
        setAffiliates(data);
        setFilteredAffiliates(data);
      } catch (err) {
        setError("Failed to load affiliate partners. Please try again later.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAffiliatePartners();
  }, []);

  // Apply all filters
  useEffect(() => {
    let filtered = [...affiliates];

    // Filter out demo accounts (emails containing "demo-")
    filtered = filtered.filter(
      (affiliate) => !affiliate.email.includes("demo-"),
    );

    // Filter out specific test emails
    const testEmails = [
      "shailpatel2+shailco@gmail.com",
      "daisy@daisy.com",
      "shail+shailco@shailppatel.com",
      "cobb@bobb.com",
      "bobleenchadha@live.com",
      "shail+test@hirelo.io",
      "abbcuk+1@outlook.com",
      "bobbyco@bobbycoo.com",
      "sohil@sohilgupta.com",
      "abbcuk+2@outlook.com",
    ];
    filtered = filtered.filter(
      (affiliate) => !testEmails.includes(affiliate.email),
    );

    // Filter out accounts created before May 20th, 2025
    const cutoffDate = new Date("2025-05-20");
    filtered = filtered.filter((affiliate) => {
      const createdDate = new Date(affiliate.created_at);
      return createdDate >= cutoffDate;
    });

    // Apply search filter
    if (searchQuery.trim()) {
      const lowercaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (affiliate) =>
          affiliate.email.toLowerCase().includes(lowercaseQuery) ||
          affiliate.referral_code.toLowerCase().includes(lowercaseQuery),
      );
    }

    // Apply date filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((affiliate) => {
        const createdDate = new Date(affiliate.created_at);
        return createdDate >= dateRange.start! && createdDate <= dateRange.end!;
      });
    }

    setFilteredAffiliates(filtered);
  }, [affiliates, searchQuery, dateRange]);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle date range filter
  const handleDateRangeChange = (
    start: Date | undefined,
    end: Date | undefined,
  ) => {
    setDateRange({ start, end });
  };

  // Format money values
  const formatMoney = (value: string) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
    }).format(parseFloat(value));
  };

  // Get status indicator based on unpaid commission
  const getPaymentStatus = (unpaidCommission: string) => {
    const amount = parseFloat(unpaidCommission);
    if (amount <= 0) {
      return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
    } else if (amount > 0 && amount < 500) {
      return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    } else {
      return <Badge variant="destructive">Due</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-gray-500">Loading affiliate partners...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg">
          <p className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center">
            <Share2 className="h-5 w-5 mr-2" />
            Affiliate Partners
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            <DateRangeFilter onFilterChange={handleDateRangeChange} />
            <SearchFilter
              onSearch={handleSearch}
              placeholder="Search partners..."
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Referral Code</TableHead>
              <TableHead className="hidden md:table-cell">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Referrals
                </div>
              </TableHead>
              <TableHead className="hidden md:table-cell">Earned</TableHead>
              <TableHead className="hidden md:table-cell">Paid</TableHead>
              <TableHead>
                <div className="flex items-center">
                  <CircleDollarSign className="w-4 h-4 mr-1" />
                  Unpaid
                </div>
              </TableHead>
              <TableHead className="hidden md:table-cell">
                <div className="flex items-center">
                  <CalendarClock className="w-4 h-4 mr-1" />
                  Joined
                </div>
              </TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAffiliates.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-6 text-gray-500"
                >
                  No affiliate partners found
                </TableCell>
              </TableRow>
            ) : (
              filteredAffiliates.map((affiliate) => (
                <TableRow key={affiliate.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium">
                    {affiliate.email}
                  </TableCell>
                  <TableCell>
                    <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                      {affiliate.referral_code}
                    </code>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {affiliate.total_referrals}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {formatMoney(affiliate.total_commission_earned)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {formatMoney(affiliate.total_commission_paid)}
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatMoney(affiliate.unpaid_commission)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {formatDateToBritish(affiliate.created_at)}
                  </TableCell>
                  <TableCell>
                    {getPaymentStatus(affiliate.unpaid_commission)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default AffiliatePartnersList;
